/* eslint-disable lingui/no-unlocalized-strings */

import { ArrowUpRight, CheckCircle, CircleNotch } from "@phosphor-icons/react";
import { Button, Dialog, DialogContent } from "@reactive-resume/ui";
import { useCallback, useEffect, useRef, useState } from "react";

import { toast } from "@/client/hooks/use-toast";
import { continueCoachPlan } from "@/client/pages/coach/optimize/saved-coach-plan";
import { debouncedUpdateResume, updateResume } from "@/client/services/resume/update";
import {
  useCoachHasJobContext,
  useCoachOptimizationStore,
} from "@/client/stores/coach-optimization";
import { useResumeStore } from "@/client/stores/resume";

import { useCoachLiveScore } from "./use-coach-live-score";
import { useCoachScoreReturnable } from "./use-coach-score-return";

// Long enough for the student to register the confirmation, short enough not to
// feel like a stall.
const RETURN_DELAY_MS = 1600;

/**
 * "Confirm And Apply" for the Coach Resume flow.
 *
 * Flushes the resume, tells the Career Coach tab which resume to bind, and closes.
 * Deliberately does NOT call the LMS itself: the student tab is already
 * authenticated there, so routing the bind through `window.opener` avoids giving
 * Coach Resume an LMS credential it would otherwise have no reason to hold.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */
export const CoachConfirmBar = () => {
  const session = useCoachOptimizationStore((state) => state.session);
  /**
   * No LMS job behind this run means no Career Coach tab to hand the résumé back to, so this bar does
   * not render at all — autosave is the whole of saving for that flow.
   *
   * ⚠️ THAT IS ONLY TRUE BECAUSE AUTOSAVE ACTUALLY WORKS NOW. It briefly rendered a plain "Save" here
   * while `/dashboard/ai-optimization` appeared not to persist accepted suggestions. The real cause was
   * elsewhere: `uploaded-resume.ts` generated item ids that failed `z.string().cuid2()`, so
   * `PATCH /resume/:id` rejected the whole document every time. With that fixed — and with autosave
   * failures now surfacing as a toast instead of being swallowed — a button whose only job is to save
   * an already-saved résumé is back to inviting the student to wait for nothing.
   */
  const hasJobContext = useCoachHasJobContext();
  const confirmed = useCoachOptimizationStore((state) => state.confirmed);
  const markConfirmed = useCoachOptimizationStore((state) => state.markConfirmed);
  const reset = useCoachOptimizationStore((state) => state.reset);

  const resume = useResumeStore((state) => state.resume);
  const setValue = useResumeStore((state) => state.setValue);
  // The SAME projection the score panel shows. It used to read useCoachProjection — the older
  // keyword/heuristic estimate — so the success modal could quote a different "25% → N%" than the
  // rings the student had just been watching.
  const projection = useCoachLiveScore();

  // Handed to the LMS so it can persist the score this panel has been showing, rather than
  // re-deriving a different one from a fresh model run. Read straight from the store: these are
  // the same two values `useCoachLiveScore` projects from, so the saved score cannot disagree
  // with the displayed one.
  const gains = useCoachOptimizationStore((state) => state.gains);
  const acceptedIds = useCoachOptimizationStore((state) => state.acceptedIds);

  // Drives the button label. Shared with the unload guard so the two cannot disagree about whether a
  // handoff is still possible.
  const returnable = useCoachScoreReturnable();

  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleConfirm = useCallback(async () => {
    if (!session || saving) return;

    setSaving(true);

    try {
      // Record the score ON THE RÉSUMÉ before the write below carries it to the server.
      //
      // The number the student has been watching lived only in the coach store — sessionStorage,
      // one tab, one visit — so a saved résumé kept no record of which job it was tuned for or what
      // that was worth. Written here rather than on each accept because this is the click that means
      // "this is the version I want": the projection moves with every card, and persisting each
      // intermediate value would store a score for a draft the student then walked back.
      //
      // `projection` is the same object the donut and the dialog below read, so the stored number
      // cannot disagree with the one they were shown.
      if (projection) {
        const currentData = useResumeStore.getState().resume.data;
        setValue(
          "metadata.coachPlan",
          continueCoachPlan({
            snapshot: currentData.metadata.coachPlan,
            context: { ...session, jobDescription: session.jobDescription ?? "" },
            resumeData: currentData,
            acceptedIds,
            score: projection.projected,
          }),
        );
        setValue("metadata.coachScore", {
          jobUuid: session.jobUuid,
          jobTitle: session.jobTitle || null,
          companyName: session.companyName || null,
          baseline: projection.baseline,
          score: projection.projected,
          badge: projection.projectedLabel,
          acceptedCount: projection.acceptedCount,
          totalCount: projection.totalCount,
          scoredAt: new Date().toISOString(),
        });
      }

      // The store autosaves on a 500ms debounce. Cancel the pending call and write
      // the current state once, so the LMS can never render a PDF from a resume
      // that is missing the student's last edit — and so the trailing debounced
      // write cannot land after we've already handed off.
      debouncedUpdateResume.cancel();
      // Read back from the store rather than using the subscribed `resume`: that binding is from
      // this callback's closure and predates the `setValue` above, so sending it would drop the
      // score from the very write meant to persist it.
      await updateResume(useResumeStore.getState().resume);

      markConfirmed();
      setDone(true);

      /**
       * ⚠️ `plan` AND `acceptedSuggestionIds` ARE WHAT MAKE THE SAVE THE SAVE.
       *
       * The LMS recomputes the score from these two — the gains the AI attributed, and which
       * of them the student accepted — using its own baseline, weights and ceiling. That is
       * how the number in the panel above becomes the number on their job card, written
       * before the bind response returns.
       *
       * Without them the LMS falls back to a full LLM rescore on its queue, which is what
       * shipped before this and produces a DIFFERENT number for the same résumé: the scorer is
       * an LLM and does not repeat itself, so the score the student watched would be replaced
       * by an unrelated one minutes later. The fallback exists for the résumé-picker recovery
       * path, which has no accepted set — it is not the intended route from this button.
       *
       * Safe to send from the browser because the LMS treats it as a SELECTOR, not a score:
       * non-editable components stay pinned to the student's baseline, each editable sub-score
       * clamps at 100, and the total clamps to their `projectedCeiling`. A tampered plan
       * reaches that ceiling and no further — exactly what accepting every genuine suggestion
       * would have achieved.
       */
      const payload = {
        type: "COACH_RESUME_READY" as const,
        jobUuid: session.jobUuid,
        coachResumeId: resume.id,
        slug: resume.slug,
        username: resume.user?.username,
        title: resume.title,
        plan: { gains },
        acceptedSuggestionIds: acceptedIds,
      };

      // Explicit target origin, never "*": this message names a resume to attach to a job
      // application, so it must not be readable by any other embedder.
      //
      // ⚠️ THIS IS THE ONLY THING THAT TRIGGERS THE RESCORE. The Career Coach tab binds the résumé
      // and the LMS queues the scoring off the back of it — so when this message does not land,
      // nothing downstream runs and the symptom is "the job score never syncs". Both failure modes
      // below used to be silent `if` conditions; they now say so, because from the student's side a
      // dropped message is indistinguishable from a broken score calculation.
      const opener = window.opener as Window | null;
      const canReturn = Boolean(opener && !opener.closed);

      if (canReturn && session.returnOrigin) {
        opener?.postMessage(payload, session.returnOrigin);
      } else {
        // Operator-facing diagnostics, not UI copy.
        console.error(
          canReturn
            ? "[CoachResume] No returnOrigin in the session, so the resume was saved but not handed back to Career Coach. Set COACH_RESUME_RETURN_ORIGIN on the LMS."
            : "[CoachResume] The Career Coach tab is gone, so the resume was saved but not handed back. It can still be attached from the resume picker.",
        );

        toast({
          variant: "error",
          title: "Saved, but not attached yet",
          description:
            "Your resume is saved. Go back to Career Coach and pick it from the resume list to attach it to this job.",
        });
      }

      timerRef.current = setTimeout(() => {
        reset();

        if (window.opener && !window.opener.closed) {
          window.close();
        }
      }, RETURN_DELAY_MS);
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (error as Error)?.message ||
        "We couldn't save your resume. Please try again.";

      toast({ variant: "error", title: "Couldn't save your resume", description: message });
      setSaving(false);
    }
  }, [session, saving, resume, gains, acceptedIds, projection, setValue, markConfirmed, reset]);

  // Also what scopes `metadata.coachScore` to the student-side flow: with no LMS job there is no
  // Confirm button, so the write above cannot run for a dashboard optimization.
  if (!session || !hasJobContext) return null;

  return (
    <>
      <Button size="lg" className="w-full" disabled={saving || confirmed} onClick={handleConfirm}>
        {/* One label per OUTCOME, not per state of the accepted set. It used to swap to "Confirm And
            Apply" once anything was accepted, which made the same button read as two different
            commitments and overstated the stronger one — the click saves the résumé and hands it back
            to Career Coach, it does not submit an application.
            "Continue" is the handoff. With no Career Coach tab to return to there is nothing to
            continue TO, so the label drops to plain "Save" rather than promising a step that cannot
            happen. The student can still attach this résumé from the résumé picker afterwards. */}
        {returnable ? "Save and Continue" : "Save"}

        {/* Icon trails the label. The spinner takes the same slot so the text does not shift
            sideways when the click starts saving. */}
        {saving ? (
          <CircleNotch size={16} className="ml-2 animate-spin" />
        ) : (
          <ArrowUpRight size={16} className="ml-2" />
        )}
      </Button>

      <Dialog open={done}>
        <DialogContent className="max-w-sm border-none [&>button]:hidden">
          <div className="flex flex-col items-center gap-y-3 py-4 text-center">
            <CheckCircle size={52} weight="fill" className="text-success" />

            <h3 className="text-lg font-semibold">Resume Updated Successfully!</h3>
            <p className="text-sm text-foreground/60">
              Your optimized resume has been saved and is ready to use.
            </p>

            {/* No longer hedged as "(projected)". The LMS now stores THIS number, computed from
                the same gains and the same accepted set this panel projected from, so it is the
                score that will be on the job card rather than an estimate of it. */}
            {projection && (
              <p className="text-sm font-medium">
                {Math.round(projection.baseline)}% → {Math.round(projection.projected)}%{" "}
                <span className="text-xs font-normal text-foreground/50">
                  (your new match score)
                </span>
              </p>
            )}

            <p className="mt-1 text-xs text-foreground/50">Taking you back to Career Coach.</p>

            <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-foreground/10">
              <div
                className="h-full bg-success transition-[width] ease-linear"
                style={{ width: done ? "100%" : "0%", transitionDuration: `${RETURN_DELAY_MS}ms` }}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
